import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useErrorService, useUserService } from '@abroad/components'
import API from '../../utils/API'
import CompletedListView from './CompletedListView'
import SchedulingTabFilters from './SchedulingTabFilters'
import { transformEventToBooking } from './scheduledTabHelpers'

const CompletedTab = ({ timezone, isCalendarConnected, onFilterRender }) => {
  const { user } = useUserService()
  const Error = useErrorService()
  const [schedulingList, setSchedulingList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const currentPage = useRef(1)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [filterUI, setFilterUI] = useState(null)

  const coachId = user?._id

  const fetchSchedulingList = useCallback(
    async (loadMore = false, page = 1) => {
      // Only fetch if coachId, timezone, and calendar is connected
      if (!coachId || !timezone || !isCalendarConnected) return

      // Reset list if not loading more
      if (!loadMore) {
        setSchedulingList([])
        currentPage.current = 1
        setIsLoading(true)
      } else {
        setIsLoadingMore(true)
      }

      try {
        const queryParams = {
          limit: 20,
          page,
          status: 'completed',
        }
        if (selectedClientId) {
          queryParams.clientId = selectedClientId
        }

        const { data } = await API.AdminAPI.scheduling.getCoachScheduledList(
          queryParams,
        )

        if (data?.success && data?.events) {
          const transformedEvents = data.events.map((event) =>
            transformEventToBooking(event, timezone, 'completed'),
          )
          if (loadMore) {
            setSchedulingList((prev) => [...prev, ...transformedEvents])
          } else {
            setSchedulingList(transformedEvents)
          }

          // Set pagination state from pagination object
          const pagination = data?.pagination || {}
          const apiTotalPages = pagination.totalPages || 0
          const apiCurrentPage = pagination.page || page
          currentPage.current = apiCurrentPage
          // hasMore is true if current page is less than total pages
          setHasMore(apiCurrentPage < apiTotalPages)
        } else {
          if (!loadMore) {
            setSchedulingList([])
          }
          setHasMore(false)
        }
      } catch (error) {
        Error.showError(error)
        if (!loadMore) {
          setSchedulingList([])
        }
        setHasMore(false)
      } finally {
        if (loadMore) {
          setIsLoadingMore(false)
        } else {
          setIsLoading(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coachId, timezone, isCalendarConnected, selectedClientId],
  )

  useEffect(() => {
    // Only fetch when all conditions are met: coachId, timezone, and calendar is connected
    if (coachId && timezone && isCalendarConnected) {
      fetchSchedulingList(false)
    }
  }, [
    coachId,
    timezone,
    isCalendarConnected,
    selectedClientId,
    fetchSchedulingList,
  ])

  const handleClientChange = useCallback((clientId) => {
    setSelectedClientId(clientId)
  }, [])

  const handleFilterRender = useCallback(
    (ui) => {
      setFilterUI(ui)
      if (onFilterRender) {
        onFilterRender(ui)
      }
    },
    [onFilterRender],
  )

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading && !isLoadingMore) {
      fetchSchedulingList(true, currentPage.current + 1)
    }
  }, [hasMore, isLoading, isLoadingMore, fetchSchedulingList])

  return (
    <>
      <SchedulingTabFilters
        selectedClientId={selectedClientId}
        onClientChange={handleClientChange}
        onFilterRender={handleFilterRender}
        isLoading={isLoading}
        disabled={false}
      />
      <CompletedListView
        schedulingList={schedulingList}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        timezone={timezone}
        clearFilter={filterUI?.clearFilter}
        isCalendarConnected={isCalendarConnected}
      />
    </>
  )
}

export default CompletedTab
